/**
 * Structured client-side logger
 *
 * Creates module-scoped loggers with levels: debug, info, warn, error.
 * In production, debug logs are suppressed. Info+ logs are sent as
 * Sentry breadcrumbs when Sentry is initialized.
 */

const LOG_LEVELS = { debug: 0, info: 1, warn: 2, error: 3 };

const MIN_LEVEL = import.meta.env.PROD ? 'info' : 'debug';

/**
 * Create a scoped logger for a module
 * @param {string} module - Module name (e.g., 'inviteService', 'AuthContext')
 * @returns {Object} Logger with debug, info, warn, error methods
 */
export const createLogger = (module) => {
  const shouldLog = (level) => LOG_LEVELS[level] >= LOG_LEVELS[MIN_LEVEL];

  const formatMessage = (level, message) => `[${level.toUpperCase()}] [${module}] ${message}`;

  const addSentryBreadcrumb = (level, message, data) => {
    if (typeof window !== 'undefined' && window.__SENTRY_INITIALIZED__) {
      import('@sentry/react').then(({ addBreadcrumb }) => {
        addBreadcrumb({
          category: module,
          message,
          level,
          data,
        });
      });
    }
  };

  return {
    debug: (message, data) => {
      if (!shouldLog('debug')) return;
      // eslint-disable-next-line no-console
      console.debug(formatMessage('debug', message), data ?? '');
    },

    info: (message, data) => {
      if (!shouldLog('info')) return;
      // eslint-disable-next-line no-console
      console.info(formatMessage('info', message), data ?? '');
      addSentryBreadcrumb('info', message, data);
    },

    warn: (message, data) => {
      if (!shouldLog('warn')) return;
      console.warn(formatMessage('warn', message), data ?? '');
      addSentryBreadcrumb('warning', message, data);
    },

    error: (message, data) => {
      if (!shouldLog('error')) return;
      console.error(formatMessage('error', message), data ?? '');
      addSentryBreadcrumb('error', message, data);
    },
  };
};
